import * as THREE from 'three';
import { Object3D } from "./Viewer.Object3D.js";

import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';

import * as turf from '@turf/turf';

const _noise = 0.0001;

class InternalForceHelper extends Object3D {
    constructor(xValues, yValues, yPeakValues, xPeaks, type, lineMaterial, meshMaterial, color) {
        super();

        this.type = type;

        this.yPeakValues = yPeakValues;

        this.objects = [];

        this.meshMaterial = meshMaterial;
        this.lineMaterial = lineMaterial;
        this.color= color;

        const xMax = xValues[xValues.length - 1];

        const shapeCoords = [];

        let prevX = null, prevY = null;

        if (this.meshMaterial) {
            xValues.forEach((x, i) => {
                let y = yValues[i];

                if (y === 0) {
                    // if the y value is exactly 0, the unkink polygon
                    // algorithm does not work => add little noise
                    y += _noise;
                }
                // Only add if not a duplicate of the previous
                if (x !== prevX || y !== prevY) {
                    shapeCoords.push([x, y]);
                    // Update previous values
                    prevX = x;
                    prevY = y;
                }
            });

            // Check if the first coordinate is not [0, 0]
            if (!(shapeCoords[0][0] === 0 && shapeCoords[0][1] === 0)) {
                shapeCoords.unshift([0, 0]);
            }

            // Check if the last coordinate is not [xMax, 0]
            if (!(shapeCoords[shapeCoords.length - 1][0] === xMax && shapeCoords[shapeCoords.length - 1][1] === 0)) {
                shapeCoords.push([xMax, 0]);
            }

            // Close the polygon
            shapeCoords.push([0, 0]);
        } else {
            xValues.forEach((x, i) => {
               shapeCoords.push([x, yValues[i]]);
            });
        }


        if (this.type === 'moment') {
            shapeCoords.forEach(point => {
                point[1] = -point[1];
            });
        }
        if (this.meshMaterial) {
            try {
                const forcePolygon = turf.polygon( [ shapeCoords ] );
                const splitPolygons = turf.unkinkPolygon( forcePolygon );

                splitPolygons.features.forEach(feature => {
                    const coordinates = feature.geometry.coordinates[0];

                    const shapePoints = coordinates.map(point => new THREE.Vector2(point[0], point[1]));
                    const shape = new THREE.Shape( shapePoints );
                    const shapeGeometry = new THREE.ShapeGeometry( shape );
                    const polygonMesh = new THREE.Mesh( shapeGeometry, this.meshMaterial );

                    this.add( polygonMesh );
                    this.objects.push( polygonMesh );
                });
            } catch (error) {
                console.error('An error occured:', error.message);
            }
        }

        const shapePoints = shapeCoords.map(point => new THREE.Vector2(point[0], point[1]));
        const lineGeo = new THREE.BufferGeometry().setFromPoints( shapePoints );
        const line = new THREE.Line( lineGeo, this.lineMaterial );
        line.computeLineDistances();

        this.add( line );
        this.objects.push( line );

        this.divs = [];
        this.labels = [];

        this.yPeakValues.forEach((y, i) => {
            if (!(isCloseToZero(y))) {
                const x = xPeaks[i];
                const text = `${ ( y ).toFixed( 2 ) }`;

                const points = [];
                points.push( new THREE.Vector3( x, 0, 0 ) );
                if (this.type === 'moment') {
                    points.push( new THREE.Vector3( x, -y, 0 ) );
                } else {
                    points.push( new THREE.Vector3( x, y, 0 ) );
                }

                if (this.meshMaterial) {
                    const geometry = new THREE.BufferGeometry().setFromPoints( points );
                    const line = new THREE.Line( geometry, this.lineMaterial );
                    this.add( line );
                }

                const div = this.createLabelElement( text );
                this.divs.push( div );

                const label = new CSS2DObject( div );
                if (this.type === 'moment') {
                    label.position.set( x, -y, 0 );
                } else {
                    label.position.set( x, y, 0 );
                }
                this.labels.push( label );
                this.add( label );
            }
        });


        this.setDirection( );
        this.setScale( 1 );
        this.setColor( color );

    }

    setColor( color ) {
        this.divs.forEach(div => {
            div.style.color = color;
        });
    }

    setDirection( ) {
        if (this.type === 'moment') {
            // this.objects.forEach(object => object.rotateX( - Math.PI / 2 ) )
            this.rotateX(  Math.PI / 2 );
        } else {
            this.rotateX( Math.PI / 2 );
        }
    }

    setScale( scaleFactor ) {
        this.scale.set( 1, scaleFactor, 1 ); // TODO: Hope this will work
        // this.updateLabelPosition(); // TODO: Maybe don't need this?
    }

    createLabelElement( text ) {
        const div = document.createElement('div');

        div.className = 'force-label';
        div.textContent = text;
        div.style.marginTop = '-1em';
        div.style.padding = '2px 4px';
        div.style.borderRadius = '4px';
        div.style.fontSize = '12px';

        return div;
    }
}

function isCloseToZero(value, epsilon = 1e-6) {
    return Math.abs(value) < epsilon;
}

export { InternalForceHelper };